
import { useSearchParams, useNavigate, useLocation } from "react-router";
import { useCallback } from "react";

export interface FilterValues {
  dateFrom: string;
  dateTo: string;
  accountName: string;
  platform: string;
  postType: string;
}

const STORAGE_KEY = "cg-filters";

function defaults(): FilterValues {
  const today = new Date().toISOString().split("T")[0];
  const ago = new Date(Date.now() - 14 * 86400000).toISOString().split("T")[0];
  return { dateFrom: ago, dateTo: today, accountName: "all", platform: "all", postType: "all" };
}

interface StoredFilters {
  dateFrom?: string;
  dateTo?: string;
}

export function useSharedFilters(): [FilterValues, (f: FilterValues) => void] {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const pathname = useLocation().pathname;

  // Only date range is cross-page (you want the same period when switching platforms).
  // accountName / platform / postType are per-page concerns — they live in the URL only,
  // never in localStorage, and reset to "all" when navigating to another page.
  const saved = typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
  const stored: StoredFilters = saved ? JSON.parse(saved) : {};
  const def = defaults();

  const filters: FilterValues = {
    dateFrom: searchParams.get("from") || stored.dateFrom || def.dateFrom,
    dateTo: searchParams.get("to") || stored.dateTo || def.dateTo,
    accountName: searchParams.get("account") || def.accountName,
    platform: searchParams.get("platform") || def.platform,
    postType: searchParams.get("type") || def.postType,
  };

  const setFilters = useCallback((f: FilterValues) => {
    // Persist ONLY date range across pages.
    const toStore: StoredFilters = { dateFrom: f.dateFrom, dateTo: f.dateTo };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toStore));

    // All filters travel through URL (so deep-linking works for current page).
    const params = new URLSearchParams();
    if (f.dateFrom !== def.dateFrom) params.set("from", f.dateFrom);
    if (f.dateTo !== def.dateTo) params.set("to", f.dateTo);
    if (f.accountName !== "all") params.set("account", f.accountName);
    if (f.platform !== "all") params.set("platform", f.platform);
    if (f.postType !== "all") params.set("type", f.postType);

    const qs = params.toString();
    navigate(qs ? `${pathname}?${qs}` : pathname, { replace: true });
  }, [navigate, pathname, def.dateFrom, def.dateTo]);

  return [filters, setFilters];
}
